import { fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useEffect, useCallback, useContext } from 'react'
import cookie from 'react-cookies'
import ProductIndexView from './ProductIndexView'
import usePagination from '../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../constants/constant'

const ProductIndexApp = () => {
  const [isFetching, setIsFetching] = useState(true)
  const [data, setData] = useState(null)
  const [title, setTitle] = useState('')
  const [brands, setBrands] = useState([])
  const [categories, setCategories] = useState([])
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [filterQuery, setFilterQuery] = useState('')
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [searchInput, setSearchInput] = useState('')
  const [filterVersion, setFilterVersion] = useState(0)
  const [triggerSearch, setTriggerSearch] = useState(false)
  const [byBrand, setByBrand] = useState('')
  const [brandName, setBrandName] = useState('')
  const [byCategory, setByCategory] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [filters, setFilters] = useState(new Map())
  const [searchApplied, setSearchApplied] = useState(false)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    setByBrand(urlParams.get('by_brand') || '')
    setBrandName(urlParams.get('brand_name') || '')
    setByCategory(urlParams.get('by_category') || '')
    setCategoryName(urlParams.get('category_name') || '')
  }, [])

  const prepareFilterData = () => {
    let localLabelToIdMapping = {}
    let uniqueDescriptions = new Set()

    const brandOptions =
      brands
        ?.filter((brand) => {
          if (!uniqueDescriptions.has(brand.description)) {
            uniqueDescriptions.add(brand.description)
            localLabelToIdMapping[brand.description] = brand.id
            return true
          }
          return false
        })
        .map((brand) => ({ label: brand.description, value: brand.id })) || []

    uniqueDescriptions.clear()

    const categoryOptions =
      categories
        ?.filter((category) => {
          if (!uniqueDescriptions.has(category.description)) {
            uniqueDescriptions.add(category.description)
            localLabelToIdMapping[category.description] = category.id
            return true
          }
          return false
        })
        .map((category) => ({ label: category.description, value: category.id })) || []

    setLabelToIdMapping(localLabelToIdMapping)

    const newFilters = [
      { key: 'Marca', value: brandOptions.map((option) => option.label) },
      { key: 'Categoría', value: categoryOptions.map((option) => option.label) }
    ]

    setFetchDataStatus({ ...fetchDataStatus, filters: true })
    setFilters(newFilters)
  }

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = selectedLabels || []
      const selectedIds = labels.map((label) => labelToIdMapping[label])
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
    pagination.current_page = 1
    buildFiltersQuery()
  }

  const buildFiltersQuery = () => {
    let queryParam = ''
    for (const [key, value] of allSelectedOptions) {
      if (value.length !== 0) {
        if (key === 'Marca') queryParam += `&brands=${value.join(',')}`
        else if (key === 'Categoría') queryParam += `&category=${value.join(',')}`
      }
    }
    setFilterQuery(queryParam)
  }

  const resetReactFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setSearchInput('')
    setSearchApplied(false)
    setFilterVersion((prevVersion) => prevVersion + 1)
    setTriggerSearch((prev) => !prev)
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  useEffect(() => {
    prepareFilterData()
  }, [brands, categories])

  useEffect(() => {
    const controller = new AbortController()

    async function fetchFilters() {
      try {
        const response = await fetchWithAuth(
          `${BASE_BACKEND_URL}/products?brand_names_only=true&business_tenant_uid=${cookie.load('business_tenant_uid')}`,
          {
            method: 'GET',
            signal: controller.signal,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        )
        const data = await response
        setBrands(data.table.brands)
        setCategories(data.table.categories)
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Fetch error:', error)
      } finally {
        setFetchDataStatus({ ...fetchDataStatus, filters: false })
      }
    }

    fetchFilters()
    return () => controller.abort('Aborted fetchFilters on cleanup.')
  }, [])

  const fetchProductsFromServer = useCallback(async (signal) => {
    const per_page = pagination.per_page
    const page = pagination.current_page
    const urlParams = new URLSearchParams(window.location.search)
    const by_brand = urlParams.get('by_brand')
    const by_category = urlParams.get('by_category')

    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/products?page=${page}&per_page=${per_page}${filterQuery ? '&filter_search=true' : ''}${filterQuery}${searchInput ? `&q=${searchInput}` : ''}${by_brand ? `&by_brand=${by_brand}` : ''}${by_category ? `&by_category=${by_category}` : ''}`,
        {
          method: 'GET',
          signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      const data = await response
      let title = 'Productos'

      if (searchInput) title = `Resultados de la búsqueda: ${searchInput}`
      if (by_brand) title = `Productos: Marca - ${urlParams.get('brand_name')}`
      if (by_category) title = `Productos: Categoría - ${urlParams.get('category_name')}`

      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(data['count'] / per_page)
      }))
      setData(data['table'])
      setIsFetching(false)
      setTitle(title)
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error:', error)
    }
  }, [pagination.current_page, filterQuery, searchInput])

  useEffect(() => {
    const controller = new AbortController()
    fetchProductsFromServer(controller.signal)
    return () => controller.abort('Aborted fetchProductsFromServer on cleanup.')
  }, [pagination.current_page, triggerSearch, filterQuery])

  useEffect(() => {
    if (allSelectedOptions.size > 0) buildFiltersQuery()
  }, [allSelectedOptions])

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
    pagination.current_page = 1
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      pagination.current_page = 1
      setSearchApplied(searchInput.trim() !== '')
      setTriggerSearch((prev) => !prev)
    }
  }

  const isAnyFilterSelected = () => {
    const hasActiveFilters = Array.from(allSelectedOptions.values()).some(
      (options) => options.length > 0
    )
    return hasActiveFilters || searchApplied
  }

  return (
    <ProductIndexView
      fetchDataStatus={fetchDataStatus}
      productData={data}
      pagination={pagination}
      handlePaginationClick={handlePaginationClick}
      filters={filters}
      updateAllSelectedOptions={updateAllSelectedOptions}
      resetReactFilters={resetReactFilters}
      isAnyFilterSelected={isAnyFilterSelected()}
      handleSearch={handleSearch}
      handleEnterSearch={handleEnterSearch}
      isFetching={isFetching}
      title={title}
      searchInput={searchInput}
      filterVersion={filterVersion}
      productFilter={{
        byBrand,
        brandName,
        byCategory,
        categoryName
      }}
    />
  )
}

export default ProductIndexApp
